import {AfterViewInit, Component, OnInit} from '@angular/core';
import {HttpErrorResponse} from '@angular/common/http';
import {DataService} from '../../../../Services/data.service';
import {ActivatedRoute, Router} from '@angular/router';
import {StorageService} from '../../../../Services/storage.service';
import {AccountMarginModel} from '../../../../DataModels/account-margin-model';
import {UntypedFormBuilder, UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {Observable} from 'rxjs';
import {ConfirmDialogService} from '../../../../Services/confirm-dialog.service';
import {BsToastService} from '../../../../Services/bs-toast-service';
import {Title} from '@angular/platform-browser';
import {NavigationService} from '../../../../Services/navigation.service';

@Component({
  selector: 'app-account-margin-form',
  templateUrl: './account-margin-form.component.html',
  styleUrls: ['./account-margin-form.component.css'],
  standalone: false
})
export class AccountMarginFormComponent implements OnInit, AfterViewInit {
  buttonConfig: any = {
    save: true,
    close: true,
    reset: true,
    delete: true,
    saveNew: true,
    saveClose: true,
    print: false,
  };

  accountMargin: AccountMarginModel;
  accountMarginForm: UntypedFormGroup;
  accountMargins$: AccountMarginModel[];
  accountMarginCount = 0;
  marginID;
  submitted = false;

  // Unsaved-changes baseline snapshot
  originalSnapshot: any = {};

  constructor(private table: DataService,
              private notification: BsToastService,
              private aRouter: Router,
              private aStorage: StorageService,
              private formBuilder: UntypedFormBuilder,
              private route: ActivatedRoute,
              private router: Router,
              private confirmDialogService: ConfirmDialogService,
              private titleService: Title,
              private nav: NavigationService) {
    this.createForm();
    this.marginID = this.route.snapshot.params.id;
  }

  createForm() {
    this.accountMarginForm = this.formBuilder.group({
      id: new UntypedFormControl(''),
      name: new UntypedFormControl(''),
      parent_id: new UntypedFormControl(null),
      sort_order: new UntypedFormControl(''),
     // audit_sequence: new FormControl(''),
      active: new UntypedFormControl('true'),
    });
  }

  ngOnInit() {
    this.loadMargins();
    if (this.marginID) {
      this.loadMargin();
      this.buttonConfig.reset = false;
    } else {
      // New form: establish a pristine baseline after initial form is created
      Promise.resolve().then(() => {
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
    }
  }

  ngAfterViewInit() {
    this.titleService.setTitle($localize`:Account Margin @@accountMargin:Account Margin`);
  }
  get f(){
    return this.accountMarginForm.controls;
  }
  private loadMargin() {
    this.table.getData('account_margin', this.marginID).subscribe(
      result => {
        this.accountMargin = result;
      },
      (error: HttpErrorResponse) => {
        // this.notification.showErrorToast($localize`:Error loading Account Margins @@errorloadingAccountMargins:Error loading Account Margins`);
        console.log(error);
      },
      () => {
        this.accountMarginForm.patchValue(this.accountMargin);
        // Baseline after fully patching the form with loaded data
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
  }

  private loadMargins() {
   this.table.getApiListData('account_margin').subscribe((res: any) => {
     this.accountMargins$ = res.Data as AccountMarginModel[];
    });
  }

  saveClose() {
    if ( this.onSave()) {
      // Re-baseline so navigation won't prompt
      try {
        this.originalSnapshot = this.buildSnapshot();
      } catch {
      }
      this.onClose();
    }
  }

  saveNew() {
    if (this.onSave()) {
      this.accountMarginForm.reset();
      try {
        this.originalSnapshot = this.buildSnapshot();
      } catch {
      }
    }
  }

  deleteItem() {
    let retMsg = '';
    const messages: string[] = [$localize`:Are you sure you want to delete account margin @@areYouSureYouWantToDeleteAccountMargin:Are you sure you want to delete account margin?`, this.accountMargin.name];
    this.confirmDialogService.confirmThis(messages,
      () => {
        this.table.deleteDataById('account_margin', this.marginID).subscribe(
          result => {
            retMsg = result;
            // this.notification.success($localize`:Deleted @@deleted:Deleted`);
          },
          (error: HttpErrorResponse) => {
            // this.notification.showErrorToast($localize`:Error @@error:Error`);
            console.log(error);
          },
          () => {
            this.onClose();
          });
      }, () => {
        this.notification.showSuccessToast($localize`:Delete Canceled @@deleteCanceled:Delete Canceled`);
      });
  }

  resetForm() {
    this.accountMarginForm.reset();
  }

  onClose() {
    const fallback = '/admin/account-margin-list';
    this.nav.backOr(fallback);
  }

  onSave() {
    this.submitted = true;
    if (this.accountMarginForm.invalid) {
      this.notification.showInfoToast($localize`:Form Errors! @@formErrors:Form Errors!`);
      return;
    }
    this.accountMargin = Object.assign({}, this.accountMarginForm.value);
    if (this.marginID) {
      // TODO probably can move invalid out side if and capture both
      if (this.accountMarginForm.invalid === false) {
        this.table.patch('account_margin', this.marginID, this.accountMargin).then();
        // this.notification.success($localize`:Updated @@updated:Updated`);
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
        return true;
      }
    } else {
      // console.log('accountMargin: ', this.accountMargin);
      this.table.postWithPromise('account_margin', this.accountMargin).then(res => {
        // stops double posting when clicking Save
        let apiReturn: any = null;
        if (typeof res === 'object') {
          apiReturn = JSON.parse(JSON.stringify(res));
        }
        this.marginID = apiReturn.id;
        try {
          this.originalSnapshot = this.buildSnapshot();
        } catch {
        }
      });
      // this.notification.success($localize`:Saved @@saved:Saved`);
      return true;
    }
    this.submitted = false;
    return true;
  }

  // =====================
  // Unsaved-changes support
  // =====================
  buildSnapshot(): any {
    try {
      return this.accountMarginForm.getRawValue();
    } catch {
      return {};
    }
  }

  hasChanges(): boolean {
    try {
      const current = this.buildSnapshot();
      const original = this.originalSnapshot || {};
      return JSON.stringify(current) !== JSON.stringify(original);
    } catch {
      return false;
    }
  }

  // Hook for UnsavedChangesGuard
  checkForChanges(): boolean {
    return this.hasChanges();
  }

}
